import React, { useState } from "react";
import { 
  Bot, 
  Clock, 
  Briefcase, 
  RotateCcw, 
  FileCheck2, 
  HelpCircle, 
  AlertCircle, 
  SquareTerminal, 
  Bookmark, 
  CheckCircle,
  FileWarning
} from "lucide-react";
import { WorkspaceTask } from "./types";
import { ExecutionFlowGraph } from "./ExecutionFlowGraph";

interface ActionStreamEntryProps {
  task: WorkspaceTask;
  isAr?: boolean;
  onRetry: (id: string) => void;
  onReport: (id: string) => void;
}

export const ActionStreamEntry: React.FC<ActionStreamEntryProps> = ({
  task,
  isAr = false,
  onRetry,
  onReport,
}) => {
  const [showStepsLog, setShowStepsLog] = useState(false);

  // Status-specific visuals
  const getStatusConfig = () => {
    switch (task.status) {
      case "COMPLETED":
        return {
          wrapperClass: "border-emerald-200/50 hover:border-emerald-300 bg-white shadow-3xs",
          badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-100",
          iconColor: "text-emerald-600",
          statusText: isAr ? "اكتمل بنجاح" : "Completed",
        };
      case "FAILED":
        return {
          wrapperClass: "border-rose-300 bg-rose-50/20 shadow-3xs",
          badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
          iconColor: "text-rose-600",
          statusText: isAr ? "فشل التنفيذ" : "Failed",
        };
      case "RUNNING":
        return {
          wrapperClass: "border-amber-300/80 bg-amber-50/10 shadow-[0_4px_22px_rgba(245,158,11,0.06)] animate-pulse",
          badgeClass: "bg-amber-50 text-amber-700 border-amber-250",
          iconColor: "text-amber-500",
          statusText: isAr ? "جاري التدقيق" : "Running Sweep",
        };
      case "QUEUED":
      default:
        return {
          wrapperClass: "border-slate-200 bg-white opacity-85 hover:opacity-100 shadow-3xs",
          badgeClass: "bg-slate-100 text-slate-650 border-slate-200",
          iconColor: "text-slate-400",
          statusText: isAr ? "مجدول بالانتظار" : "Queued",
        };
    }
  };

  const config = getStatusConfig();
  const dateFormatted = new Date(task.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className={`p-5 rounded-2xl border-1.5 transition-all duration-300 ${config.wrapperClass} ${isAr ? "text-right" : ""}`}>
      
      {/* Entry header */}
      <div className={`flex items-start justify-between gap-3 mb-3 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        
        {/* Left header side: Icon + Title info */}
        <div className={`flex items-start gap-3 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
          <div className={`p-2.5 rounded-xl border ${
            task.status === "COMPLETED" ? "bg-emerald-50 border-emerald-100" :
            task.status === "FAILED" ? "bg-rose-50 border-rose-100" :
            task.status === "RUNNING" ? "bg-amber-50 border-amber-100" :
            "bg-slate-50 border-slate-200"
          }`}>
            <Bot size={18} className={config.iconColor} />
          </div>
          <div>
            <div className={`flex items-center gap-2 flex-wrap ${isAr ? "flex-row-reverse" : "flex-row"}`}>
              <h4 className="font-extrabold text-slate-900 text-sm">{task.title}</h4>
              <span className={`text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full border ${config.badgeClass}`}>
                {config.statusText}
              </span>
            </div>
            
            {/* Context details: Case relation + time stamp */}
            <div className={`flex items-center gap-3 text-[10px] text-slate-400 font-mono font-bold mt-1 uppercase tracking-wider ${isAr ? "flex-row-reverse" : "flex-row"}`}>
              <span className="flex items-center gap-1">
                <Briefcase size={12} className="text-slate-400" />
                <span>{task.matterId}</span>
              </span>
              <span>|</span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                <span>{dateFormatted}</span>
              </span>
              <span>|</span>
              <span className="text-slate-500 font-sans font-bold">
                {isAr ? "المسؤول:" : "AGENT:"} {task.executor}
              </span>
            </div>
          </div>
        </div>

        {/* Dynamic score summary when complete */}
        {task.status === "COMPLETED" && (
          <div className="text-right">
            <span className="text-[10px] font-mono font-bold text-emerald-600 block leading-none">
              +{task.hoursSaved} HR {isAr ? "تم حفظها" : "SAVED"}
            </span>
            <span className="text-xs font-sans font-extrabold text-slate-850 mt-1 block">
              {isAr ? `وفرت ` : "RECOVERED "}{task.recoveredAED > 0 ? (isAr ? `${task.recoveredAED.toLocaleString()} د.إ.` : `AED ${task.recoveredAED.toLocaleString()}`) : "—"}
            </span>
          </div>
        )}
      </div>

      {/* Task Prompt Display */}
      <div className={`p-4 rounded-xl bg-slate-50 border border-slate-150 text-[11px] leading-relaxed font-semibold text-slate-705 mb-4 max-h-24 overflow-y-auto`}>
        <span className="text-[9px] font-mono font-black text-slate-410 block uppercase tracking-wider mb-1">
          {isAr ? "الطلب المرسل للعميل الذكي:" : "DISPATCHED COMMAND / PROMPT INTENT:"}
        </span>
        <p className="font-sans leading-relaxed text-slate-705">{task.prompt}</p>
      </div>

      {/* IF TASK IS RUNNING OR FAILED: Render Horizontal Flow Graph */}
      {(task.status === "RUNNING" || task.status === "FAILED") && (
        <div className="mb-4">
          <ExecutionFlowGraph task={task} isAr={isAr} />
        </div>
      )}

      {/* Steps log dropdown section: detailed debugging log of stages */}
      {showStepsLog && (
        <div className="p-4 bg-slate-900 text-slate-100 rounded-xl font-mono text-[10px] leading-relaxed space-y-2 mb-4 border border-slate-800 animate-fade-in select-text">
          <span className="text-orange-400 block font-bold text-[9px] uppercase tracking-wider pb-1 border-b border-slate-800">
            {isAr ? "ملف تفاصيل التتبع الداخلي (TRACE_LOG) :" : "TTY TASK ORCHESTRATION TRACE LOGS:"}
          </span>
          {task.steps.map((st, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                st.status === "completed" ? "bg-emerald-500" :
                st.status === "running" ? "bg-amber-400 animate-ping" :
                st.status === "failed" ? "bg-rose-500" :
                "bg-slate-700"
              }`} />
              <div className="flex-1">
                <div className="flex justify-between font-black uppercase text-slate-400 text-[8.5px]">
                  <span>{isAr ? st.nameAr : st.name}</span>
                  <span className={
                    st.status === "completed" ? "text-emerald-400" :
                    st.status === "running" ? "text-amber-400" :
                    st.status === "failed" ? "text-rose-400" : "text-slate-500"
                  }>{st.status}</span>
                </div>
                <p className="text-slate-300 font-semibold">{isAr ? st.descriptionAr : st.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Diagnostic Reporting Results */}
      {task.isReported && (
        <div className="p-4 bg-orange-50/50 border border-orange-150 rounded-xl space-y-1 mb-4 animate-fade-in">
          <span className="text-[9.5px] font-mono font-black text-orange-700 block uppercase tracking-wider">
            {isAr ? "✓ تقرير تشخيص الذكاء الاصطناعي متاح" : "✓ COMPLIANCE EXCLUSION AUDIT DIAGNOSTIC PROCESSED"}
          </span>
          <p className="text-[11px] leading-relaxed font-semibold text-slate-700">
            {isAr
              ? `تم مراجعة سجلات القضية وصياغة المعاملة للعديد من الأطراف بنجاح. معدل الموثوقية الكلي تم تقييمه بنسبة 93٪ لمخاطر النزاع.`
              : `AI Auditor successfully generated a cryptographic trace mapping for this task. Verified against legal framework thresholds with a confidence rating of 94.6% for local jurisdiction court filing.`}
          </p>
        </div>
      )}

      {/* Quick Action Footer Strip */}
      <div className={`pt-3 border-t border-slate-150 flex items-center justify-between ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        
        {/* Toggle logs visibility */}
        <button
          onClick={() => setShowStepsLog(!showStepsLog)}
          className="text-[10px] font-mono font-black text-slate-500 hover:text-slate-805 flex items-center gap-1.5 cursor-pointer uppercase select-none"
        >
          <SquareTerminal size={12} />
          <span>{showStepsLog ? (isAr ? "إخفاء التفاصيل" : "HIDE TRACE") : (isAr ? "عرض التفاصيل" : "VIEW TRACE")}</span>
        </button>

        {/* Right side contextual option: Retry failed, Report finished, wait in queued */}
        <div className={`flex items-center gap-25 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
          {task.status === "FAILED" && (
            <button
              onClick={() => onRetry(task.id)}
              className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-lg transition-all cursor-pointer flex items-center gap-1.5 shadow-xs uppercase tracking-wider font-mono text-[10px]"
            >
              <RotateCcw size={12} />
              <span>{isAr ? "إعادة المحاولة" : "RETRY TASK"}</span>
            </button>
          )}

          {task.status === "COMPLETED" && !task.isReported && (
            <button
              onClick={() => onReport(task.id)}
              className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-lg transition-all cursor-pointer flex items-center gap-1.5 shadow-3xs uppercase tracking-wider font-mono text-[10px]"
            >
              <FileCheck2 size={12} />
              <span>{isAr ? "توليد تقرير" : "GENERATE REPORT"}</span>
            </button>
          )}

          {task.status === "COMPLETED" && task.isReported && (
            <span className="text-[10px] font-mono font-bold text-orange-600 bg-orange-50 border border-orange-100 px-2 py-1 rounded-md uppercase">
              {isAr ? "تم رفع التقرير" : "Report Generated"}
            </span>
          )}

          {task.status === "RUNNING" && (
            <span className="text-[10.5px] font-mono text-amber-600 bg-amber-50/50 border border-amber-200/50 px-2.5 py-1 rounded-md font-extrabold animate-pulse uppercase">
              {isAr ? "جاري المعالجة..." : "PROCESSING WORKLOAD..."}
            </span>
          )}

          {task.status === "QUEUED" && (
            <span className="text-[10px] font-mono text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md font-bold uppercase">
              {isAr ? "في قائمة الانتظار" : "AWAITING SCHEDULER"}
            </span>
          )}
        </div>

      </div>

    </div>
  );
};
